/* @ts-ignore - this is all nextauth code */
/* eslint-disable */
import type { CookieOption, LoggerInstance } from 'next-auth';
import type { NextRequest } from 'next/server';
import type { NextApiRequest } from 'next';

const ALLOWED_COOKIE_SIZE = 4096;
// Based on commented out section above
const ESTIMATED_EMPTY_COOKIE_SIZE = 163;
const CHUNK_SIZE = ALLOWED_COOKIE_SIZE - ESTIMATED_EMPTY_COOKIE_SIZE;

interface Cookie extends CookieOption {
  value: string;
}

type Chunks = Record<string, string>;

export class SessionStore {
  #chunks: Chunks = {};
  #option: CookieOption;
  #logger: LoggerInstance | Console;

  constructor(
    option: CookieOption,
    req: Partial<{
      cookies: NextRequest['cookies'] | NextApiRequest['cookies'];
      headers: NextRequest['headers'] | NextApiRequest['headers'];
    }>,
    logger: LoggerInstance | Console
  ) {
    this.#logger = logger;
    this.#option = option;

    const { cookies } = req;
    const { name: cookieName } = option;

    // @ts-ignore - because this is nextauth code
    if (typeof cookies?.getAll === 'function') {
      // Next.js ^v13.0.1 (Edge Env)
      // @ts-ignore - nextauth code here
      for (const { name, value } of cookies.getAll()) {
        if (name.startsWith(cookieName)) {
          this.#chunks[name] = value;
        }
      }
    } else if (cookies instanceof Map) {
      for (const name of cookies.keys()) {
        // @ts-ignore
        if (name.startsWith(cookieName)) this.#chunks[name] = cookies.get(name);
      }
    } else {
      for (const name in cookies) {
        // @ts-ignore
        if (name.startsWith(cookieName)) this.#chunks[name] = cookies[name];
      }
    }
  }

  /**
   * The JWT Session or database Session ID
   * constructed from the cookie chunks.
   */
  get value() {
    // Sort the chunks by their keys before joining
    const sortedKeys = Object.keys(this.#chunks).sort((a, b) => {
      const aSuffix = parseInt(a.split('.').pop() ?? '0');
      const bSuffix = parseInt(b.split('.').pop() ?? '0');

      return aSuffix - bSuffix;
    });

    // Use the sorted keys to join the chunks in the correct order
    return sortedKeys.map((key) => this.#chunks[key]).join('');
  }

  /** Given a cookie, return a list of cookies, chunked to fit the allowed cookie size. */
  #chunk(cookie: Cookie): Cookie[] {
    const chunkCount = Math.ceil(cookie.value.length / CHUNK_SIZE);

    if (chunkCount === 1) {
      this.#chunks[cookie.name] = cookie.value;
      return [cookie];
    }

    const cookies: Cookie[] = [];
    for (let i = 0; i < chunkCount; i++) {
      const name = `${cookie.name}.${i}`;
      const value = cookie.value.substr(i * CHUNK_SIZE, CHUNK_SIZE);
      cookies.push({ ...cookie, name, value });
      this.#chunks[name] = value;
    }

    this.#logger.debug('CHUNKING_SESSION_COOKIE', {
      message: `Session cookie exceeds allowed ${ALLOWED_COOKIE_SIZE} bytes.`,
      emptyCookieSize: ESTIMATED_EMPTY_COOKIE_SIZE,
      valueSize: cookie.value.length,
      chunks: cookies.map((c) => c.value.length + ESTIMATED_EMPTY_COOKIE_SIZE),
    });

    return cookies;
  }

  /** Returns cleaned cookie chunks. */
  #clean(): Record<string, Cookie> {
    const cleanedChunks: Record<string, Cookie> = {};
    for (const name in this.#chunks) {
      delete this.#chunks?.[name];
      cleanedChunks[name] = {
        name,
        value: '',
        options: { ...this.#option.options, maxAge: 0 },
      };
    }
    return cleanedChunks;
  }

  /**
   * Given a cookie value, return new cookies, chunked, to fit the allowed cookie size.
   * If the cookie has changed from chunked to unchunked or vice versa,
   * it deletes the old cookies as well.
   */
  chunk(value: string, options: Partial<Cookie['options']>): Cookie[] {
    // Assume all cookies should be cleaned by default
    const cookies: Record<string, Cookie> = this.#clean();

    // Calculate new chunks
    const chunked = this.#chunk({
      name: this.#option.name,
      value,
      options: { ...this.#option.options, ...options },
    });

    // Update stored chunks / cookies
    for (const chunk of chunked) {
      cookies[chunk.name] = chunk;
    }

    return Object.values(cookies);
  }

  /** Returns a list of cookies that should be cleaned. */
  clean(): Cookie[] {
    return Object.values(this.#clean());
  }
}
